import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { FiltresGalerie, FiltresGalerieDispo, GalerieService, PhotoGalerie } from 'src/app/core/services/entities/galerie.service';
import { FermeStateService } from 'src/app/core/services/entities/ferme-state.service';

interface JourGalerie { cle: string; libelle: string; photos: { p: PhotoGalerie; i: number }[]; }
interface MoisGalerie { cle: string; libelle: string; jours: JourGalerie[]; }

/**
 * Grille de la Galerie Wanar : vignettes carrées par mois puis par jour (date de prise), filtres (module,
 * élément, auteur, période), défilement infini. Mode sélection : un clic choisit la photo (sélecteur
 * « Galerie Wanar ») ; sinon il ouvre la visionneuse.
 */
@Component({
  selector: 'bay-galerie',
  template: `
    <div class="bg-filtres mb-3" data-etat="filtres-galerie">
      <div *ngIf="dispo?.modules?.length" class="d-flex flex-wrap gap-2 mb-2" role="group" aria-label="Filtrer par module">
        <button type="button" class="btn btn-sm rounded-pill" [class.btn-success]="!filtres.module" [class.btn-outline-secondary]="filtres.module"
                [attr.aria-pressed]="!filtres.module" (click)="appliquer({ module: undefined, lienType: undefined, lienId: undefined })" data-module="TOUS">Tout</button>
        <button *ngFor="let m of dispo!.modules" type="button" class="btn btn-sm rounded-pill" [attr.data-module]="m.code"
                [class.btn-success]="filtres.module === m.code" [class.btn-outline-secondary]="filtres.module !== m.code"
                [attr.aria-pressed]="filtres.module === m.code"
                (click)="appliquer({ module: filtres.module === m.code ? undefined : m.code, lienType: undefined, lienId: undefined })">
          {{ m.label }} <span class="opacity-75">· {{ m.nombre }}</span>
        </button>
      </div>
      <div class="row g-2 align-items-end">
        <div class="col-12 col-md-4" *ngIf="elements.length">
          <label class="form-label small fw-semibold mb-1" [for]="idBase + '-element'">Élément</label>
          <select [id]="idBase + '-element'" class="form-select form-select-sm rounded-3" [ngModel]="cleElement" (ngModelChange)="choisirElement($event)">
            <option value="">Tous les éléments</option>
            <option *ngFor="let e of elements" [value]="e.lienType + ':' + e.lienId">{{ e.label }} ({{ e.nombre }})</option>
          </select>
        </div>
        <div class="col-12 col-md-3" *ngIf="dispo?.auteurs?.length">
          <label class="form-label small fw-semibold mb-1" [for]="idBase + '-auteur'">Auteur</label>
          <select [id]="idBase + '-auteur'" class="form-select form-select-sm rounded-3" [ngModel]="filtres.auteur || ''" (ngModelChange)="appliquer({ auteur: $event || undefined })">
            <option value="">Tout le monde</option>
            <option *ngFor="let a of dispo!.auteurs" [value]="a.id">{{ a.nom }} ({{ a.nombre }})</option>
          </select>
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label small fw-semibold mb-1" [for]="idBase + '-du'">Du</label>
          <input [id]="idBase + '-du'" type="date" class="form-control form-control-sm rounded-3" [ngModel]="filtres.du || ''"
                 [min]="jour(dispo?.plusAncienne)" [max]="jour(dispo?.plusRecente)" (change)="appliquer({ du: $any($event.target).value || undefined })">
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label small fw-semibold mb-1" [for]="idBase + '-au'">Au</label>
          <input [id]="idBase + '-au'" type="date" class="form-control form-control-sm rounded-3" [ngModel]="filtres.au || ''"
                 [min]="jour(dispo?.plusAncienne)" [max]="jour(dispo?.plusRecente)" (change)="appliquer({ au: $any($event.target).value || undefined })">
        </div>
        <div class="col-12 col-md-1" *ngIf="aDesFiltres">
          <button type="button" class="btn btn-sm btn-link text-success p-0" (click)="effacer()">Effacer les filtres</button>
        </div>
      </div>
    </div>

    <p class="small text-muted mb-2" *ngIf="total != null" data-etat="compteur-galerie" aria-live="polite">{{ total }} photo{{ total > 1 ? 's' : '' }}</p>
    <div *ngIf="erreur" class="alert alert-warning rounded-3" role="alert">{{ erreur }}</div>

    <div *ngIf="chargement && !photos.length" class="bg-grille" aria-hidden="true">
      <span *ngFor="let s of squelette" class="bg-case bg-squelette"></span>
    </div>

    <div *ngIf="!chargement && !erreur && !photos.length" class="text-center py-5" data-etat="galerie-vide">
      <span class="material-symbols-outlined d-block mb-2" style="font-size:56px;color:#cbd5e1;">photo_library</span>
      <h2 class="h6 fw-semibold text-muted">{{ aDesFiltres ? 'Aucune photo pour ces filtres' : 'Pas encore de photo' }}</h2>
      <p *ngIf="!aDesFiltres" class="small text-muted mb-0">Les photos prises sur les fiches et par l'équipe arriveront ici.</p>
    </div>

    <section *ngFor="let m of mois; trackBy: parCle" class="mb-3" [attr.data-mois]="m.cle">
      <h2 class="bg-mois">{{ m.libelle }}</h2>
      <div *ngFor="let j of m.jours; trackBy: parCle" class="mb-2" [attr.data-jour]="j.cle">
        <h3 class="bg-jour">{{ j.libelle }}</h3>
        <div class="bg-grille">
          <button *ngFor="let x of j.photos; trackBy: parId" type="button" class="bg-case" (click)="cliquer(x.i)"
                  [attr.aria-label]="(selection ? 'Choisir ' : 'Voir ') + (x.p.legende || x.p.nom || 'la photo')" [attr.data-photo]="x.p.id">
            <img *ngIf="(vignette(x.p) | async) as s" [src]="s" alt="" loading="lazy">
          </button>
        </div>
      </div>
    </section>
    <div #sentinelle class="bg-sentinelle" aria-hidden="true"></div>
    <div *ngIf="chargement && photos.length" class="text-center my-3"><span class="spinner-border spinner-border-sm text-success" role="status"></span></div>

    <bay-visionneuse *ngIf="!selection && ouverte != null" [photos]="photos" [index]="ouverte" [total]="total ?? undefined"
                     (indexChange)="ouverte = $event" (fermer)="ouverte = null" (plus)="suite()" (supprimee)="retirer($event)"></bay-visionneuse>
  `,
  styles: [`
    .bg-grille { display:grid; grid-template-columns:repeat(auto-fill, minmax(130px, 1fr)); gap:4px; }
    @media (max-width: 575.98px) { .bg-grille { grid-template-columns:repeat(3, 1fr); } }
    .bg-case { aspect-ratio:1; border:none; padding:0; background:#e5e7eb; overflow:hidden; border-radius:6px; }
    .bg-case img { width:100%; height:100%; object-fit:cover; display:block; transition:transform .2s; }
    .bg-case:hover img { transform:scale(1.04); }
    .bg-case:focus-visible { outline:3px solid #0d631b; outline-offset:2px; }
    .bg-squelette { animation:bg-pulse 1.2s ease-in-out infinite; }
    @keyframes bg-pulse { 50% { opacity:.5; } }
    @media (prefers-reduced-motion: reduce) { .bg-squelette { animation:none; } .bg-case img { transition:none; } }
    .bg-mois { font-size:16px; font-weight:800; color:#181d17; margin:8px 0 6px; }
    .bg-jour { font-size:13px; font-weight:600; color:#4b5563; margin:8px 0 6px; }
    .bg-sentinelle { height:1px; }
  `]
})
export class GalerieComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Mode sélection : un clic choisit la photo */
  @Input() selection = false;
  /** Filtres de départ (ex. adresse de la page) */
  @Input() filtresInitiaux: FiltresGalerie = {};
  @Output() choisir = new EventEmitter<PhotoGalerie>();
  /** Filtres changés (la page les reporte dans l'adresse) */
  @Output() filtresChange = new EventEmitter<FiltresGalerie>();
  @ViewChild('sentinelle') sentinelle!: ElementRef<HTMLElement>;

  readonly idBase = 'galerie-' + Math.random().toString(36).slice(2, 7);
  readonly squelette = Array.from({ length: 18 });
  filtres: FiltresGalerie = {};
  dispo: FiltresGalerieDispo | null = null;
  photos: PhotoGalerie[] = [];
  mois: MoisGalerie[] = [];
  total: number | null = null;
  curseur: string | null = null;
  chargement = false;
  erreur: string | null = null;
  ouverte: number | null = null;

  private vignettes = new Map<number, Observable<SafeUrl | null>>();
  private requete?: Subscription;
  private observateur?: IntersectionObserver;

  constructor(private galerie: GalerieService, private fermeState: FermeStateService, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.filtres = { ...this.filtresInitiaux };
    this.galerie.filtres(this.fermeState.fermeId).subscribe({ next: d => this.dispo = d, error: () => this.dispo = null });
    this.charger(true);
  }

  ngAfterViewInit(): void {
    if (typeof IntersectionObserver === 'undefined') { return; }
    this.observateur = new IntersectionObserver(e => { if (e.some(x => x.isIntersecting)) { this.suite(); } }, { rootMargin: '600px' });
    this.observateur.observe(this.sentinelle.nativeElement);
  }

  ngOnDestroy(): void { this.requete?.unsubscribe(); this.observateur?.disconnect(); }

  get elements() {
    const l = this.dispo?.elements || [];
    return this.filtres.module ? l.filter(e => e.module === this.filtres.module) : l;
  }

  get cleElement(): string { return this.filtres.lienType ? `${this.filtres.lienType}:${this.filtres.lienId}` : ''; }

  get aDesFiltres(): boolean {
    const f = this.filtres;
    return !!(f.module || f.lienType || f.auteur || f.du || f.au);
  }

  jour(d?: string): string | null { return d ? d.slice(0, 10) : null; }

  appliquer(c: Partial<FiltresGalerie>): void {
    this.filtres = { ...this.filtres, ...c };
    this.filtresChange.emit(this.filtres);
    this.charger(true);
  }

  choisirElement(v: string): void {
    if (!v) { this.appliquer({ lienType: undefined, lienId: undefined }); return; }
    const [lienType, lienId] = v.split(':');
    this.appliquer({ lienType, lienId });
  }

  effacer(): void { this.filtres = {}; this.filtresChange.emit({}); this.charger(true); }

  suite(): void { if (!this.chargement && this.curseur && !this.erreur) { this.charger(false); } }

  charger(depuisDebut: boolean): void {
    this.requete?.unsubscribe();
    if (depuisDebut) { this.photos = []; this.mois = []; this.curseur = null; this.total = null; this.ouverte = null; }
    this.chargement = true;
    this.erreur = null;
    this.requete = this.galerie.page(this.fermeState.fermeId, this.filtres, depuisDebut ? null : this.curseur).subscribe({
      next: p => {
        this.photos = [...this.photos, ...(p.items || [])];
        this.curseur = p.curseurSuivant || null;
        if (p.total != null) { this.total = p.total; } else if (!this.curseur) { this.total = this.photos.length; }
        this.grouper();
        this.chargement = false;
      },
      error: e => {
        this.chargement = false;
        this.erreur = e?.status === 403 ? (e?.error?.message || 'La Galerie n\'est pas ouverte à votre rôle.') : 'Impossible de charger la Galerie. Réessayez.';
      }
    });
  }

  cliquer(i: number): void {
    if (this.selection) { this.choisir.emit(this.photos[i]); } else { this.ouverte = i; }
  }

  retirer(id: number): void {
    const i = this.photos.findIndex(p => p.id === id);
    if (i < 0) { return; }
    this.photos = this.photos.filter(p => p.id !== id);
    if (this.total != null) { this.total--; }
    this.grouper();
    this.ouverte = this.photos.length ? Math.min(i, this.photos.length - 1) : null;
  }

  vignette(p: PhotoGalerie): Observable<SafeUrl | null> {
    let o = this.vignettes.get(p.id);
    if (!o) {
      o = this.galerie.miniature(p.id).pipe(map(u => u ? this.sanitizer.bypassSecurityTrustUrl(u) : null));
      this.vignettes.set(p.id, o);
    }
    return o;
  }

  private grouper(): void {
    const auj = this.cle(new Date());
    const h = new Date(); h.setDate(h.getDate() - 1);
    const hier = this.cle(h);
    const mois: MoisGalerie[] = [];
    this.photos.forEach((p, i) => {
      const d = new Date(p.datePrise || p.dateEnvoi || 0);
      const cleJ = this.cle(d), cleM = cleJ.slice(0, 7);
      let m = mois[mois.length - 1];
      if (!m || m.cle !== cleM) {
        const lib = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        m = { cle: cleM, libelle: lib.charAt(0).toUpperCase() + lib.slice(1), jours: [] };
        mois.push(m);
      }
      let j = m.jours[m.jours.length - 1];
      if (!j || j.cle !== cleJ) {
        const lib = cleJ === auj ? 'Aujourd\'hui' : cleJ === hier ? 'Hier' : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
        j = { cle: cleJ, libelle: lib, photos: [] };
        m.jours.push(j);
      }
      j.photos.push({ p, i });
    });
    this.mois = mois;
  }

  private cle(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  parCle(_: number, x: { cle: string }): string { return x.cle; }
  parId(_: number, x: { p: PhotoGalerie }): number { return x.p.id; }
}
